import { css, FlattenSimpleInterpolation, keyframes } from 'styled-components';

export default {
  easeInOutCubic: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
};

export function fadeInLeft(
  inView: boolean,
  amount: number,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: transform ${duration}ms ${delay}ms ${easingFunc}, opacity ${duration}ms ${delay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
          transform: translateX(0px);
        `
      : css`
          opacity: 0;
          transform: translateX(-${amount}px);
        `}
  `;
}

export function fadeIn(
  inView: boolean,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: opacity ${duration}ms ${delay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
        `}
  `;
}

export function fadeInUp(
  inView: boolean,
  amount: number,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: transform ${duration}ms ${delay}ms ${easingFunc}, opacity ${duration}ms ${delay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
          transform: translateY(0px);
        `
      : css`
          opacity: 0;
          transform: translateY(${amount}px);
        `}
  `;
}

export function scaleFadeIn(
  inView: boolean,
  amount: number,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: transform ${duration}ms ${delay}ms ${easingFunc}, opacity ${duration}ms ${delay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
          transform: scale(1);
        `
      : css`
          opacity: 0;
          transform: scale(${amount});
        `}
  `;
}

export function fadeInDown(
  inView: boolean,
  amount: number,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: transform ${duration}ms ${delay}ms ${easingFunc}, opacity ${duration}ms ${delay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
          transform: translateY(0px);
        `
      : css`
          opacity: 0;
          transform: translateY(-${amount}px);
        `}
  `;
}

export function slideInUp(
  inView: boolean,
  duration: number,
  delay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    overflow: hidden;

    span {
      display: inline-block;
      transition: transform ${duration}ms ${delay}ms ${easingFunc}, opacity ${duration}ms ${delay}ms ${easingFunc};
    }

    ${inView
      ? css`
          span {
            opacity: 1;
            transform: translateY(0px);
          }
        `
      : css`
          span {
            opacity: 0;
            transform: translateY(100%);
          }
        `}
  `;
}

export function greyscaleImgFade(
  inView: boolean,
  duration: number,
  delay: number,
  filterDelay: number,
  easingFunc: string,
): FlattenSimpleInterpolation {
  return css`
    transition: opacity ${duration}ms ${delay}ms ${easingFunc}, filter ${duration}ms ${filterDelay}ms ${easingFunc};

    ${inView
      ? css`
          opacity: 1;
          filter: grayscale(0);
        `
      : css`
          opacity: 0;
          filter: grayscale(1);
        `}
  `;
}

const shimmerKeyframes = keyframes`
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
`;

export const shimmer = css`
  animation: ${shimmerKeyframes} 2s infinite linear;
  background: linear-gradient(to right, #eff1f3 4%, #e2e2e2 25%, #eff1f3 36%);
  background-size: 1000px 100%;
`;

const fadeInAnimationKeyframes = keyframes`
0% {
  opacity: 0;
}
100% {
  opacity: 1;
}
`;

const shakeKeyframes = keyframes`
  10%, 90% {
    transform: translateX(-1px);
  }
  
  20%, 80% {
    transform: translateX(2px);
  }

  30%, 50%, 70% {
    transform: translateX(-4px);
  }

  40%, 60% {
    transform: translateX(4px);
  }
`;

export const shake = css`
  animation: ${shakeKeyframes} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
`;

export function fadeInKeyframes(duration: number, delay: number, easingFunc: string): FlattenSimpleInterpolation {
  return css`
    opacity: 0;
    animation: ${fadeInAnimationKeyframes} ${duration}ms ${delay}ms ${easingFunc} forwards;
  `;
}

export function loadingEllipsis(delay?: number): FlattenSimpleInterpolation {
  return css`
    @keyframes ellipsis {
      to {
        width: 1.5em;
      }
    }

    &[data-loading='true'],
    [data-loading='true'] & {
      &::after {
        content: '...';
        overflow: hidden;
        display: inline-block;
        vertical-align: bottom;
        animation: ellipsis steps(4, end) 1s infinite ${delay ?? 0}s;
        width: 0px;
      }
    }
  `;
}
